import type { AxiosResponse } from "axios";

import {
  newIdempotencyKey,
  toMoneyString,
} from "@/modules/dashboard/dispersions/services/paymentsPayout.service";
import { HttpApiService } from "@/shared/services/httpApi.service";
import type { BrebReceivingKeySource } from "@/shared/services/payments/brebReceivingKey.service";
import type { FeeBreakdownTaxSnapshot, PaymentTaxLine } from "@/shared/utils/paymentTaxes";

/**
 * Integration with the NEW NestJS payments runtime for QR-initiated payins, NOT the legacy PHP proxy (`POST
 * /bra/payin/brl`). Endpoints live under `/internal/payments/payins` (JWT; permission `payments:write` to
 * initiate, `payments:read` to poll). The runtime mints the QR at the provider (dynamic Pix QR), persists the
 * payin as CREATED and settles it when the provider webhook confirms the payment — PHP is not involved at any
 * step.
 *
 * Backend contract (coloca-nest): - `POST /internal/payments/payins` → `InitiatePayinDto` /
 * `InitiatePayinResponseDto` (`src/coloca/payments/dto/initiate-payin.dto.ts`) - `GET
 * /internal/payments/payins/:id` → `PayinEntity` (`src/coloca/payments/payins/entities/payin.entity.ts`)
 */

// Mirrors coloca-nest `SUPPORTED_PAYIN_RAILS`. Brazil QR payins use PIX; Colombia uses BREB (Bre-B
// dynamic QR), PSE (bank redirect), NEQUI (push charge into the payer's Nequi app) or BANCOLOMBIA_BUTTON
// (bank redirect). SPEI has both ways in — an initiated per-payin receiving CLABE and the organization's
// standing CLABE credited by push detection. CBU_CVU is push-detected only, so it never reaches
// `initiatePayin`, but it DOES reach `previewPayin`: pricing a corridor needs a route and fees, not a
// collection instrument.
export const PayinRail = {
  PIX: "PIX",
  SPEI: "SPEI",
  BREB: "BREB",
  PSE: "PSE",
  NEQUI: "NEQUI",
  BANCOLOMBIA_BUTTON: "BANCOLOMBIA_BUTTON",
  CBU_CVU: "CBU_CVU",
} as const;
export type PayinRail = (typeof PayinRail)[keyof typeof PayinRail];

// Mirrors coloca-nest `PayinStatus`. QR flow: CREATED → (webhook settle) → PENDING → CONFIRMED.
export const PayinRuntimeStatus = {
  CREATED: "CREATED",
  PENDING: "PENDING",
  CONFIRMED: "CONFIRMED",
  REJECTED: "REJECTED",
  FAILED: "FAILED",
  REVERSED: "REVERSED",
} as const;
export type PayinRuntimeStatus = (typeof PayinRuntimeStatus)[keyof typeof PayinRuntimeStatus];

/** Payer identity the provider needs to mint the collection instrument (`InitiatePayinPayerDto`). */
export interface InitiatePayinPayer {
  /** Payer full name (<= 120 chars). The only payer field SPEI requires. */
  name: string;
  /**
   * Payer tax id (<= 20 chars). REQUIRED for PIX (CPF/CNPJ) and the Colombian payer rails (PSE, NEQUI,
   * BANCOLOMBIA_BUTTON — CC/CE/NIT/… number); optional for SPEI, where it is the RFC.
   */
  document_number?: string;
  /** PIX: CPF | CNPJ. Colombian rails: document short code (CC, CE, NIT, TI, PA). Unused by SPEI. */
  document_type?: string;
  email?: string;
  /** On NEQUI this is also the Nequi account the charge is pushed to. */
  phone?: string;
  /** PSE only: the bank the payer authorizes the payment in (code from GET /internal/payments/pse-banks). */
  bank_code?: string;
  /** PSE only: payer kind — defaults to person server-side. */
  user_type?: "person" | "company";
  /** PSE only: optional payer address. */
  address?: string;
}

/** Request body for `POST /internal/payments/payins` (InitiatePayinDto). */
export interface InitiatePayinPayload {
  organization_id: string;
  rail: PayinRail;
  country: string; // ISO 3166-1 alpha-3, e.g. "BRA"
  currency: string; // e.g. "BRL"
  gross_amount: string; // decimal string, up to 8 dp
  // Required for PIX (name + document) and SPEI (name); OMITTED for BREB — a Bre-B
  // dynamic QR needs no payer data (the payer identity arrives with the settlement webhook).
  payer?: InitiatePayinPayer;
  idempotency_key: string; // <= 64 chars, unique per operation
  /** BREB only: mint the QR over this ACTIVE organization key instead of the primary one. */
  breb_receiving_key_id?: string;
}

/** Response of the initiate endpoint (InitiatePayinResponseDto). */
export interface InitiatePayinResponse {
  id: string;
  status: PayinRuntimeStatus;
  organization_id: string;
  rail: PayinRail;
  country: string;
  currency: string;
  gross_amount: string;
  fee_total: string;
  tax_total?: string;
  taxes?: PaymentTaxLine[];
  net_amount: string;
  provider_reference?: string | null;
  /** PIX / Bre-B EMV payload, rendered as a QR client-side; only PIX offers it for pasting. */
  qr_code_payload?: string | null;
  /**
   * SPEI: the ephemeral receiving CLABE the provider minted FOR THIS PAYIN. The payer transfers to it and the
   * webhook matches the deposit back by that number — it is this rail's collection instrument, the way
   * `qr_code_payload` is PIX's. Not the organization's standing CLABE, which lives on a different surface.
   */
  clabe?: string | null;
  /**
   * PSE only: the bank-authorization page to send the payer to. May be null when the network had not
   * registered the payment yet — keep polling the payin; the payment still resolves.
   */
  redirect_url?: string | null;
  /**
   * Instrument expiry (ISO 8601) — the QR's, or the CLABE's. PSE carries none: the network resolves an
   * unfinished flow on its own timeout, and no TTL for the bank link is documented.
   */
  expires_at?: string | null;
}

/** Which amount the caller fixed on a fee preview; the other one is derived server-side. */
export const PayinPreviewDirection = {
  /** `gross_amount` given — "the payer sends X, how much lands?" */
  FIXED_GROSS: "FIXED_GROSS",
  /** `net_amount` given — "I want to receive N, what must the payer send?" */
  FIXED_NET: "FIXED_NET",
} as const;
export type PayinPreviewDirection = (typeof PayinPreviewDirection)[keyof typeof PayinPreviewDirection];

/** Request body for `POST /internal/payments/payins/preview` (PreviewPayinDto). Exactly ONE amount. */
export interface PreviewPayinPayload {
  organization_id: string;
  rail: PayinRail;
  country: string;
  currency: string;
  /** Fixed-send: what the payer will transfer. Mutually exclusive with net_amount. */
  gross_amount?: string;
  /** Fixed-receive: what must land after fees. Mutually exclusive with gross_amount. */
  net_amount?: string;
  /** Payer kind the per-transaction limits are checked against; omitted = the envelope of both kinds. */
  payer_type?: "PERSON" | "COMPANY";
}

/** Ops-facing fee split of the preview (internal response only). */
export interface PayinFeePreviewBreakdown {
  coloca_total: string;
  provider_total: string;
  economic_model: string;
}

/** Response of the preview endpoint (PreviewPayinResponseDto). All amounts are 8-dp decimal strings. */
export interface PreviewPayinResponse {
  direction: PayinPreviewDirection;
  rail: PayinRail;
  country: string;
  currency: string;
  gross_amount: string;
  /** Includes the client-borne taxes listed in `taxes`. */
  fee_total: string;
  tax_total?: string;
  taxes?: PaymentTaxLine[];
  net_amount: string;
  breakdown: PayinFeePreviewBreakdown;
}

/** How a Bre-B payin arrived. Null channel: a historic or staff-detected row that never recorded it. */
export type PayinBrebChannel = "QR" | "KEY" | "STATIC_QR";

export interface PayinBrebDetail {
  channel: PayinBrebChannel | null;
  /**
   * The organization key the payin was minted over or paid to, on every channel when the payin names one; for
   * older rows only on KEY / STATIC_QR. Never the shared business key.
   */
  receiving_key: string | null;
  /** Absent on a backend that predates multiple keys; null when the payin names no key row. */
  receiving_key_source?: BrebReceivingKeySource | null;
  receiving_key_id?: string | null;
  payer_name: string | null;
  payer_bank_name: string | null;
}

/** Subset of the returned PayinEntity the UI cares about (status polling + activity list/detail). */
export interface PayinRuntimeEntity {
  id: string;
  organization_id: string;
  country: string;
  currency: string;
  rail: PayinRail;
  status: PayinRuntimeStatus;
  /** Set on QR-initiated payins (what the payer should transfer); null on pure push-detected ones. */
  gross_amount_expected: string | null;
  gross_amount_received: string | null;
  fee_total: string;
  /** Only the taxes are read; members never receive the Coloca-borne ones. */
  fee_breakdown?: { taxes?: FeeBreakdownTaxSnapshot[] | null } | null;
  net_amount: string | null;
  provider_reference: string | null;
  /** Proof/evidence link, when the payin carries one. */
  evidence_url: string | null;
  /** Provider-side expiration of the collection instrument (QR / dynamic CLABE), ISO 8601. */
  expires_at: string | null;
  /**
   * QR rails (Bre-B, PIX): the EMV payload, so a lost initiate response does not lose the QR. Present ONLY on
   * `GET /internal/payments/payins/:id` and only while the payin can still be paid (`CREATED` and before
   * `expires_at`) — the runtime withholds it once paid or expired, and the list endpoint never sends it. So
   * treat its presence as the answer to "may I still show this QR?", not as "this payin has a QR".
   */
  qr_code_payload?: string | null;
  /** PSE only: the bank-authorization page, re-served from the rail snapshot while the payin can be paid. */
  redirect_url?: string | null;
  /**
   * PSE only (`GET /:id`): the network's result data — `trazability_code` is the CUS shown to the payer,
   * `core_id` the core receipt id (preferred over the CUS on the success receipt), `failure_detail` the
   * literal rejection cause (e.g. NOT_AUTHORIZED) the rejection screen displays verbatim.
   */
  pse?: {
    bank_code: string;
    trazability_code: string | null;
    core_id: string | null;
    failure_detail: string | null;
  } | null;
  /** Bre-B only (`GET /:id`), whatever the status: how the money arrived and from whom. */
  breb?: PayinBrebDetail | null;
  failure_reason: string | null;
  confirmed_at: string | null;
  failed_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Query params for the runtime payins list — mirrors coloca-nest `PayinsQueryDto` (all AND-combined). */
export interface ListPayinsParams {
  /**
   * Tenancy: org-scoped callers MUST pass it (an unscoped query would return every org's payins to an
   * INTERNAL user). Only INTERNAL staff views may omit it to list cross-org.
   */
  organizationId?: string | null;
  /** PayinStatus enum value (single — the API does not accept multi-select). */
  status?: PayinRuntimeStatus;
  rail?: PayinRail;
  /** ISO 3166-1 alpha-3, e.g. "MEX". */
  country?: string;
  /** E.g. "MXN". */
  currency?: string;
  /** Created_at >= from (ISO 8601). */
  from?: string;
  /** Created_at <= to (ISO 8601). */
  to?: string;
  /** `completed` makes `from` / `to` (and the order) apply to `confirmed_at` instead of `created_at`. */
  dateField?: "created" | "completed";
  /** Page size — backend default 10, max 200. */
  limit?: number;
  /** Offset — backend default 0. */
  skip?: number;
}

/** Paginated shape of the runtime list endpoints (`PaginatedResponse<T>` in coloca-nest). */
export interface PaginatedRuntimePayins {
  data: PayinRuntimeEntity[];
  total: number;
  skip: number;
  limit: number;
  hasMore: boolean;
}

/**
 * CPF = 11 digits, CNPJ = 14 digits — the rule the provider enforces to mint the dynamic QR. Shared by every
 * Brazil payin form (wallets deposit modal, /app/payins) so the validation cannot drift.
 */
export function isValidPayerDocument(document: string): boolean {
  const digits = document.replace(/\D/g, "");
  return digits.length === 11 || digits.length === 14;
}

export interface BuildBrazilPixPayinInput {
  organizationId: string;
  /** Gross BRL amount the payer transfers (fees are computed server-side over this). */
  amountBrl: number | string;
  payerName: string;
  /** CPF/CNPJ — masked or raw; normalized to digits before sending. */
  payerDocument: string;
  /** Reuse across retries of the same operation. Defaults to a fresh uuid. */
  idempotencyKey?: string;
}

/**
 * Build the InitiatePayinPayload for the Brazil / PIX corridor. `gross_amount` is what the payer transfers;
 * the runtime computes `fee_total` and `net_amount` from the payment-fees config and returns them in the
 * response — there is no client-side fee estimate.
 */
export function buildBrazilPixPayinPayload(input: BuildBrazilPixPayinInput): InitiatePayinPayload {
  const document = input.payerDocument.replace(/\D/g, "");
  return {
    organization_id: input.organizationId,
    rail: PayinRail.PIX,
    country: "BRA",
    currency: "BRL",
    gross_amount: toMoneyString(input.amountBrl),
    payer: {
      name: input.payerName.trim(),
      document_number: document,
      // Same heuristic the backend applies for the origin snapshot: 14 digits → CNPJ, else CPF.
      document_type: document.length === 14 ? "CNPJ" : "CPF",
    },
    idempotency_key: input.idempotencyKey ?? newIdempotencyKey(),
  };
}

export interface BuildColombiaBrebPayinInput {
  organizationId: string;
  /** Gross COP amount the payer transfers (fees are computed server-side over this). */
  amountCop: number | string;
  /** Reuse across retries of the same operation. Defaults to a fresh uuid. */
  idempotencyKey?: string;
  /** Another ACTIVE key of the organization; omitted, the QR is minted over the primary one. */
  brebReceivingKeyId?: string | null;
}

/**
 * Build the InitiatePayinPayload for the Colombia / Bre-B corridor. Unlike PIX/SPEI, a Bre-B dynamic QR needs
 * NO payer data — the backend exempts BREB in `validateInitiate` (the payer identity only arrives with the
 * settlement webhook). `gross_amount` is what the payer transfers; the runtime computes `fee_total` /
 * `net_amount` and returns the QR payload (`qr_code_payload`) to render client-side.
 */
export function buildColombiaBrebPayinPayload(input: BuildColombiaBrebPayinInput): InitiatePayinPayload {
  return {
    organization_id: input.organizationId,
    rail: PayinRail.BREB,
    country: "COL",
    currency: "COP",
    gross_amount: toMoneyString(input.amountCop),
    idempotency_key: input.idempotencyKey ?? newIdempotencyKey(),
    ...(input.brebReceivingKeyId ? { breb_receiving_key_id: input.brebReceivingKeyId } : {}),
  };
}

export interface BuildColombiaPsePayinInput {
  organizationId: string;
  /** Gross COP amount the payer transfers (fees are computed server-side over this). */
  amountCop: number | string;
  payerName: string;
  /** Colombian document short code (CC, CE, NIT, TI, PA). */
  documentType: string;
  documentNumber: string;
  email: string;
  phone: string;
  /** Bank code from `PseBanksService.list()`. */
  bankCode: string;
  /** Payer address — the PSE network schema requires it. */
  address: string;
  userType?: "person" | "company";
  /** Reuse across retries of the same operation. Defaults to a fresh uuid. */
  idempotencyKey?: string;
}

/** Shared payer identity of the Colombian full-payer rails (NEQUI, BANCOLOMBIA_BUTTON). */
export interface BuildColombiaPayerInput {
  organizationId: string;
  /** Gross COP amount the payer transfers (fees are computed server-side over this). */
  amountCop: number | string;
  payerName: string;
  /** Colombian document short code (CC, CE, NIT, TI, PA). */
  documentType: string;
  documentNumber: string;
  email: string;
  /** On NEQUI this is the Nequi account that receives the push charge. */
  phone: string;
  /** The Colombian networks require the payer address. */
  address: string;
  /** Reuse across retries of the same operation. Defaults to a fresh uuid. */
  idempotencyKey?: string;
}

/**
 * Build the InitiatePayinPayload for the Colombia / Nequi push corridor. The charge lands straight in the
 * payer's Nequi app (`payer.phone` is the account charged) — no redirect and no QR; the payin settles when
 * the payer accepts the push and the provider webhook confirms it.
 */
export function buildColombiaNequiPayinPayload(input: BuildColombiaPayerInput): InitiatePayinPayload {
  return {
    organization_id: input.organizationId,
    rail: PayinRail.NEQUI,
    country: "COL",
    currency: "COP",
    gross_amount: toMoneyString(input.amountCop),
    payer: {
      name: input.payerName.trim(),
      document_type: input.documentType,
      document_number: input.documentNumber.replace(/\D/g, ""),
      email: input.email.trim(),
      phone: input.phone.replace(/\D/g, ""),
      address: input.address.trim(),
    },
    idempotency_key: input.idempotencyKey ?? newIdempotencyKey(),
  };
}

/**
 * Build the InitiatePayinPayload for the Colombia / Botón Bancolombia corridor (bank redirect, PSE-shaped but
 * with no bank selector — the button IS the bank). NO `return_url` is sent: the backend falls back to its
 * environment default (the dashboard result page); unlike PSE the network redirects there verbatim, so the
 * result page resolves the payin from the context persisted before redirecting. The response carries
 * `redirect_url` — send the payer there.
 */
export function buildColombiaBancolombiaButtonPayinPayload(
  input: BuildColombiaPayerInput,
): InitiatePayinPayload {
  return {
    organization_id: input.organizationId,
    rail: PayinRail.BANCOLOMBIA_BUTTON,
    country: "COL",
    currency: "COP",
    gross_amount: toMoneyString(input.amountCop),
    payer: {
      name: input.payerName.trim(),
      document_type: input.documentType,
      document_number: input.documentNumber.replace(/\D/g, ""),
      email: input.email.trim(),
      phone: input.phone.replace(/\D/g, ""),
      address: input.address.trim(),
    },
    idempotency_key: input.idempotencyKey ?? newIdempotencyKey(),
  };
}

export interface BuildMexicoSpeiPayinInput {
  organizationId: string;
  /** Gross MXN amount the payer transfers (fees are computed server-side over this). */
  amountMxn: number | string;
  payerName: string;
  /** RFC — optional for SPEI, unlike the CPF/CNPJ a Pix QR requires. */
  payerDocument?: string;
  /** Reuse across retries of the same operation. Defaults to a fresh uuid. */
  idempotencyKey?: string;
}

/**
 * /** Build the InitiatePayinPayload for the Colombia / PSE corridor (bank redirect). The payer registers
 * with the network up-front, so the full identity + chosen bank are required. NO `return_url` is sent: the
 * backend falls back to its environment default (the dashboard result page) and its return callback appends
 * `?payin=<id>` so that page knows which payin to poll. The response carries `redirect_url` — send the payer
 * there; settlement is confirmed by the provider webhook (poll the payin until CONFIRMED / REJECTED).
 */
export function buildColombiaPsePayinPayload(input: BuildColombiaPsePayinInput): InitiatePayinPayload {
  return {
    organization_id: input.organizationId,
    rail: PayinRail.PSE,
    country: "COL",
    currency: "COP",
    gross_amount: toMoneyString(input.amountCop),
    payer: {
      name: input.payerName.trim(),
      document_type: input.documentType,
      document_number: input.documentNumber.replace(/\D/g, ""),
      email: input.email.trim(),
      phone: input.phone.replace(/\D/g, ""),
      bank_code: input.bankCode,
      address: input.address.trim(),
      user_type: input.userType ?? "person",
    },
    idempotency_key: input.idempotencyKey ?? newIdempotencyKey(),
  };
}

/**
 * Build the InitiatePayinPayload for the México / SPEI corridor.
 *
 * SPEI has TWO ways in and this is the initiated one: the provider mints an **ephemeral receiving CLABE for
 * this payin**, with an expiry, and the deposit is matched back to it by that CLABE. The other way — the
 * organization's standing CLABE, where anything that arrives is credited by push detection — needs no payin
 * at all and is what `RuntimeDepositPanel` shows.
 *
 * Only the payer NAME is required (the backend's `validateInitiate` falls through to `payer_name_required`
 * for SPEI); the RFC is optional, which is why this builder omits `document_type`.
 */
export function buildMexicoSpeiPayinPayload(input: BuildMexicoSpeiPayinInput): InitiatePayinPayload {
  const document = input.payerDocument?.replace(/\s/g, "").trim();
  return {
    organization_id: input.organizationId,
    rail: PayinRail.SPEI,
    country: "MEX",
    currency: "MXN",
    gross_amount: toMoneyString(input.amountMxn),
    payer: {
      name: input.payerName.trim(),
      ...(document ? { document_number: document } : {}),
    },
    idempotency_key: input.idempotencyKey ?? newIdempotencyKey(),
  };
}

/** Live `initiatePayinOnce` requests, keyed by idempotency key. See that method for why it exists. */
const inFlightInitiations = new Map<string, Promise<AxiosResponse<InitiatePayinResponse>>>();

/**
 * The runtime's own rejection code on a deterministic 400 (e.g. PSE's `FAIL_EXCEEDEDLIMIT`), when there is
 * one. It selects the mandated copy on the failure screens, so a generic message is never shown in its
 * place.
 */
export function readPayinErrorCode(error: unknown): string | null {
  if (!HttpApiService.isAxiosError(error)) return null;
  const data = error.response?.data as { code?: unknown } | undefined;
  return typeof data?.code === "string" ? data.code : null;
}

export class PaymentsPayinService {
  private static BASE_URL = "/internal/payments/payins";

  /**
   * Initiate a QR payin (dynamic Pix QR). The `Idempotency-Key` header is sent along with the body
   * `idempotency_key`: the header cache replays the FULL first response on a network retry, and the
   * domain-idempotency hit behind it now re-serves the instrument from the rail snapshot instead of answering
   * with a payin that carries no way to collect. Either path gives back a usable response.
   */
  static async initiatePayin(payload: InitiatePayinPayload): Promise<AxiosResponse<InitiatePayinResponse>> {
    return HttpApiService.post<InitiatePayinResponse, InitiatePayinPayload>(this.BASE_URL, payload, {
      headers: { "Idempotency-Key": payload.idempotency_key },
    });
  }

  /**
   * `initiatePayin`, but at most one live request per idempotency key.
   *
   * React StrictMode double-mounts every effect in development, and a wizard's "creating" step is an effect:
   * the second run would fire a duplicate POST, trip the server's in-progress idempotency lock and surface a
   * bogus error while the real call succeeded. Reusing the SAME promise makes the remount a no-op. Every
   * screen that creates a payin from an effect must go through here rather than re-implementing the map.
   */
  static initiatePayinOnce(payload: InitiatePayinPayload): Promise<AxiosResponse<InitiatePayinResponse>> {
    const existing = inFlightInitiations.get(payload.idempotency_key);
    if (existing) return existing;

    const request = this.initiatePayin(payload).finally(() => {
      inFlightInitiations.delete(payload.idempotency_key);
    });
    inFlightInitiations.set(payload.idempotency_key, request);
    return request;
  }

  /** Fetch a payin (org-scoped) — used to poll the status until CONFIRMED / REJECTED / FAILED. */
  static async getPayin(id: string): Promise<AxiosResponse<PayinRuntimeEntity>> {
    return HttpApiService.get<PayinRuntimeEntity>(`${this.BASE_URL}/${id}`);
  }

  /**
   * Stateless fee preview — the same route + fee rows `initiatePayin` will use, priced without minting
   * anything, so the UI can show the commission while the user types. Send exactly one of `gross_amount` /
   * `net_amount`; the backend derives the other (`direction` echoes which side was fixed). The numbers are
   * as-of-now, not frozen: creating the payin re-resolves the fee configuration.
   */
  static async previewPayin(
    payload: PreviewPayinPayload,
    signal?: AbortSignal,
  ): Promise<AxiosResponse<PreviewPayinResponse>> {
    return HttpApiService.post<PreviewPayinResponse, PreviewPayinPayload>(
      `${this.BASE_URL}/preview`,
      payload,
      {
        signal,
      },
    );
  }

  /**
   * List payins, newest first (`created_at DESC`). Used by the Mexico deposit panel and the payments activity
   * view.
   *
   * Tenancy: org-scoped callers ALWAYS pass `organizationId` explicitly (members are auto-pinned server-side,
   * but a blind unscoped query from an INTERNAL user would surface every org's money). Only INTERNAL staff
   * views may omit it to list cross-org.
   */
  static async listPayins(params: ListPayinsParams): Promise<AxiosResponse<PaginatedRuntimePayins>> {
    const query = new URLSearchParams();
    if (params.organizationId) query.set("organization_id", params.organizationId);
    if (params.status) query.set("status", params.status);
    if (params.rail) query.set("rail", params.rail);
    if (params.country) query.set("country", params.country);
    if (params.currency) query.set("currency", params.currency);
    if (params.from) query.set("from", params.from);
    if (params.to) query.set("to", params.to);
    if (params.dateField) query.set("date_field", params.dateField);
    if (params.limit != null) query.set("limit", String(params.limit));
    if (params.skip != null) query.set("skip", String(params.skip));
    const suffix = query.size > 0 ? `?${query}` : "";
    return HttpApiService.get<PaginatedRuntimePayins>(`${this.BASE_URL}${suffix}`);
  }
}
